import { ValidationError } from 'yup';
import { angularInjectorProvider } from 'Injector';
import { type ErrorLogService as ErrorLogServiceClass } from 'ErrorLogging';
import { type FormConfig } from 'FormConfigs';
import { FormProgress, type FormCompletionInput, type FormValidationError } from './FormCompletionHelper.types';

const formIsComplete = (formConfig: FormConfig, input: FormCompletionInput) => {
    // If the form has a validationSchema, the form is complete if the form values pass it.
    if (formConfig.validationSchema) {
        try {
            return formConfig.validationSchema.validateSync(input.formValues);
        } catch (error) {
            // Any other error is a bug in a schema, such as a `when` condition that dereferences a
            // sibling the caller did not supply. Report it, and count the step as incomplete.
            //
            // Do not rethrow here, as getStepValidationErrors does for the save gate. The settings
            // page computes completion while it links, so a throw would stop the page from
            // rendering. notifyInProd still throws in development, so the bug stays loud there.
            if (!(error instanceof ValidationError)) {
                const ErrorLogService = angularInjectorProvider.safeGet<typeof ErrorLogServiceClass>('ErrorLogService');
                ErrorLogService?.notifyInProd(
                    'A form validation schema threw an error that is not a ValidationError',
                    error,
                    {
                        level: 'error',
                        step_name: formConfig.stepName,
                    },
                );
            }
            return false;
        }
    }

    // If the form doesn't have a validationSchema, it's technically complete
    return true;
};

/**
 * Every validation failure for one step, rather than the pass/fail that getFormProgress reports.
 *
 * A step with no `validationSchema` returns no errors. That is the same rule getFormProgress uses -
 * such a step counts as complete - so this function never blocks submit_application.
 *
 * Yup stops at the first failure by default. We pass `abortEarly: false` to collect all of them,
 * which puts the individual failures on `inner`.
 */
export const getStepValidationErrors = (formConfig: FormConfig, input: FormCompletionInput): FormValidationError[] => {
    if (!formConfig.validationSchema) return [];

    try {
        formConfig.validationSchema.validateSync(input.formValues, { abortEarly: false });
        return [];
    } catch (error) {
        if (!(error instanceof ValidationError)) throw error;

        return error.inner.map(failure => ({
            path: failure.path ?? '',
            type: failure.type,
        }));
    }
};

export const getFormProgress = (formConfig: FormConfig, input: FormCompletionInput): FormProgress => {
    // We mark the submit_application step as incomplete if they haven't submitted
    // their application yet to create the appearance that submitting their application
    // is a required action when completing their application.
    if (formConfig.stepName === 'submit_application') {
        if (input.hasSubmittedApplication) return FormProgress.complete;
        return FormProgress.incomplete;
    }

    if (formIsComplete(formConfig, input)) {
        // a special case where a required field could be prefilled by us or if they've
        // completed the step and have cleared their localStorage (e.g. in Network,
        // pref_student_network_privacy is prefilled by us).
        // So if this step is complete, we show a check mark as the default, which is a more
        // accurate UI
        if (formConfig.requiresVisit && !input.visitedSteps.has(formConfig.stepName)) return FormProgress.none;
        return FormProgress.complete;
    }
    return FormProgress.incomplete;
};

export const getFormsProgressMap = (formConfigs: FormConfig[], input: FormCompletionInput) => {
    const formProgressMap: { [key: string]: FormProgress } = {};
    formConfigs.forEach((formConfig: FormConfig) => {
        formProgressMap[formConfig.stepName] = getFormProgress(formConfig, input);
    });
    return formProgressMap;
};

export const getFormsCompletePercent = (formConfigs: FormConfig[], input: FormCompletionInput) => {
    // Exclude any forms without a `validationSchema`, meaning the form isn't required, from the
    // complete percent calculation.
    //
    // An `optional` step is excluded as well. Such a step can carry a schema, because it can hold a
    // conditionally required input, but the user never has to answer it. Counting it here would
    // raise the percentage of a user who has answered nothing. See FormConfig.
    const requiredFormConfigs = formConfigs.filter(formConfig => formConfig.validationSchema && !formConfig.optional);
    // If there are no required form configs, return 100. In certain instances, namely Exec Ed,
    // users visit /settings/application_status to register for a program that does not support
    // applicationFormConfigs.
    if (requiredFormConfigs.length === 0) return 100;

    const formProgressMap = getFormsProgressMap(requiredFormConfigs, input);
    const complete = Object.values(formProgressMap).filter(val => val === FormProgress.complete).length;
    return Math.round((complete / requiredFormConfigs.length) * 100);
};

export const getFirstIncompleteForm = (formConfigs: FormConfig[], input: FormCompletionInput) =>
    formConfigs.find(formConfig => getFormProgress(formConfig, input) === FormProgress.incomplete);
